'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  approveBusinessRevenueCategoryMapping,
  createBusinessRevenueCategoryMapping,
  deactivateBusinessRevenueCategoryMapping,
  getBusinessReportSources,
  getBusinessRevenueCategoryMappings,
  getBusinessRevenueSources,
  updateBusinessRevenueCategoryMapping,
  type BusinessReportSource,
  type BusinessRevenueCategoryMapping,
  type BusinessRevenueCategoryMappingInput,
  type BusinessRevenueSource,
} from '../lib/api/business-reports';
import { AdminAlertModal, Loader } from './common';

const EMPTY: BusinessRevenueCategoryMappingInput = {
  dataSourceKey: '',
  sourceCategory: '',
  revenueSourceKey: '',
  revenueAmountField: 'NET_AMOUNT',
  businessDateField: 'EFFECTIVE_DATE',
  currency: 'INR',
  includedTransactionTypes: ['Bill', 'Sales Return', 'Bill Cancel'],
  effectiveFrom: null,
  effectiveTo: null,
};

export function BusinessRevenueCategoryMappingsTab({
  active,
  token,
  onTopBarActionsChange,
}: {
  active: boolean;
  token?: string;
  onTopBarActionsChange?: (actions: ReactNode) => void;
}) {
  const [rows, setRows] = useState<BusinessRevenueCategoryMapping[]>([]);
  const [sources, setSources] = useState<BusinessReportSource[]>([]);
  const [revenueSources, setRevenueSources] = useState<BusinessRevenueSource[]>([]);
  const [form, setForm] = useState<BusinessRevenueCategoryMappingInput>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [approvalId, setApprovalId] = useState<string | null>(null);
  const [approvalReference, setApprovalReference] = useState('');
  const [busy, setBusy] = useState('');
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setBusy('Loading governed category rules...');
    try {
      const [mappings, dataSources, governedRevenueSources] = await Promise.all([
        getBusinessRevenueCategoryMappings(token),
        getBusinessReportSources(token),
        getBusinessRevenueSources(token),
      ]);
      setRows(mappings);
      setSources(dataSources.filter((source) => ['CSV_IMPORT', 'EXCEL_IMPORT'].includes(source.sourceType)));
      setRevenueSources(governedRevenueSources.filter((source) => source.sourceMode === 'AUTOMATIC'));
      setForm((current) => ({
        ...current,
        dataSourceKey: current.dataSourceKey || dataSources.find((source) => source.sourceKey === 'MEDICS_REVENUE_REPORT_BY_BILL')?.sourceKey || '',
        revenueSourceKey: current.revenueSourceKey || governedRevenueSources.find((source) => source.sourceKey === 'MAIN')?.sourceKey || '',
      }));
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load category rules', message: message(error) });
    } finally {
      setBusy('');
    }
  }, [token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  const selectedSource = useMemo(
    () => sources.find((source) => source.sourceKey === form.dataSourceKey),
    [form.dataSourceKey, sources],
  );

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;
    onTopBarActionsChange(
      <div
        className="business-calendar-top-status"
        title="Approved mappings remain active; creating or editing a mapping requires separate checker approval."
      >
        <span>Governance safeguard</span>
        <strong className="business-source-status active">ENFORCED</strong>
      </div>,
    );
    return () => onTopBarActionsChange(null);
  }, [active, onTopBarActionsChange]);

  if (!active) return null;

  const reset = () => {
    setEditingId(null);
    setForm({
      ...EMPTY,
      dataSourceKey: sources.find((source) => source.sourceKey === 'MEDICS_REVENUE_REPORT_BY_BILL')?.sourceKey || sources[0]?.sourceKey || '',
      revenueSourceKey: revenueSources.find((source) => source.sourceKey === 'MAIN')?.sourceKey || revenueSources[0]?.sourceKey || '',
    });
  };

  const save = async () => {
    setBusy(editingId ? 'Saving draft rule...' : 'Creating draft rule...');
    try {
      const input = {
        ...form,
        sourceCategory: form.sourceCategory.trim(),
        currency: form.currency.trim().toUpperCase(),
        includedTransactionTypes: form.includedTransactionTypes.map((value) => value.trim()).filter(Boolean),
        effectiveFrom: form.effectiveFrom || null,
        effectiveTo: form.effectiveTo || null,
      };
      if (editingId) await updateBusinessRevenueCategoryMapping(editingId, input, token);
      else await createBusinessRevenueCategoryMapping(input, token);
      reset();
      await load();
      setAlert({
        type: 'success',
        title: editingId ? 'Rule returned to draft' : 'Draft rule created',
        message: 'A different Business Reports manager must approve the rule before it can become active.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Rule was not saved', message: message(error) });
    } finally {
      setBusy('');
    }
  };

  const approve = async () => {
    if (!approvalId) return;
    setBusy('Approving and activating category rule...');
    try {
      await approveBusinessRevenueCategoryMapping(approvalId, approvalReference, token);
      setApprovalId(null);
      setApprovalReference('');
      await load();
      setAlert({ type: 'success', title: 'Rule approved', message: 'The category mapping is now active with immutable audit history.' });
    } catch (error) {
      setAlert({ type: 'error', title: 'Approval failed', message: message(error) });
    } finally {
      setBusy('');
    }
  };

  return (
    <section className="tab-content business-source-page" id="tab-business-revenue-category-rules">
      {busy && <Loader message={busy} />}
      <div className="enterprise-card business-source-form-card business-category-rule-form-card">
        <div className="enterprise-card-heading business-category-rule-form-heading">
          <div>
            <strong>{editingId ? 'Edit rule as a new draft' : 'Create category rule'}</strong>
            <span>Approved rules remain active. Creating or editing a rule produces a new draft for separate checker approval.</span>
          </div>
          <span className="business-definition-draft-badge governed">Governed changes</span>
        </div>
        <div className="business-source-form-grid business-category-rule-form-grid">
          <label className="business-source-field"><span>File source</span>
            <select value={form.dataSourceKey} onChange={(event) => setForm({ ...form, dataSourceKey: event.target.value })}>
              <option value="">Select source</option>
              {sources.map((source) => <option key={source.sourceKey} value={source.sourceKey}>{source.sourceName}{source.isActive ? '' : ' · preview only'}</option>)}
            </select>
          </label>
          <label className="business-source-field"><span>Source Category (exact value)</span>
            <input value={form.sourceCategory} onChange={(event) => setForm({ ...form, sourceCategory: event.target.value })} placeholder="MAIN or PHARMACY" maxLength={120} />
          </label>
          <label className="business-source-field"><span>Revenue stream</span>
            <select value={form.revenueSourceKey} onChange={(event) => setForm({ ...form, revenueSourceKey: event.target.value })}>
              <option value="">Select revenue stream</option>
              {revenueSources.map((source) => <option key={source.sourceKey} value={source.sourceKey}>{source.sourceName}</option>)}
            </select>
          </label>
          <label className="business-source-field"><span>Revenue amount field</span>
            <select value={form.revenueAmountField} onChange={(event) => setForm({ ...form, revenueAmountField: event.target.value as 'NET_AMOUNT' | 'TAXABLE_AMOUNT' })}>
              <option value="NET_AMOUNT">Net Amt</option>
              <option value="TAXABLE_AMOUNT">Taxable Amt</option>
            </select>
          </label>
          <label className="business-source-field"><span>Business-date field</span>
            <select value={form.businessDateField} onChange={(event) => setForm({ ...form, businessDateField: event.target.value as 'DOCUMENT_DATE' | 'EFFECTIVE_DATE' })}>
              <option value="EFFECTIVE_DATE">Effective Date</option>
              <option value="DOCUMENT_DATE">Document Date</option>
            </select>
          </label>
          <label className="business-source-field"><span>Currency</span>
            <input value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value })} maxLength={3} />
          </label>
          <label className="business-source-field"><span>Included transaction types (comma-separated)</span>
            <input value={form.includedTransactionTypes.join(', ')} onChange={(event) => setForm({ ...form, includedTransactionTypes: event.target.value.split(',') })} />
          </label>
          <label className="business-source-field"><span>Effective from</span>
            <input type="date" value={form.effectiveFrom || ''} onChange={(event) => setForm({ ...form, effectiveFrom: event.target.value || null })} />
          </label>
          <label className="business-source-field"><span>Effective to</span>
            <input type="date" value={form.effectiveTo || ''} onChange={(event) => setForm({ ...form, effectiveTo: event.target.value || null })} />
          </label>
        </div>
        <div className="business-source-form-actions">
          <span>A separate checker must approve this draft before the rule can become active.</span>
          <div>
            {editingId && <button className="btn btn-outline" onClick={reset}>Cancel edit</button>}
            <button className="btn btn-primary" disabled={!selectedSource || !form.sourceCategory.trim() || !form.revenueSourceKey || !form.includedTransactionTypes.some((value) => value.trim())} onClick={() => void save()}>
              {editingId ? 'Save as draft' : 'Create draft'}
            </button>
          </div>
        </div>
      </div>

      <div className="enterprise-card business-category-rule-table-card">
        <div className="enterprise-card-heading">
          <div><strong>Governed category rules</strong><span>Active rules determine the approved field, transaction types and effective period for each exact source category.</span></div>
          <span className="business-source-count">{rows.length} rules</span>
        </div>
        <div className="enterprise-table-wrap">
          <table className="business-source-table business-category-rule-table">
            <thead><tr><th>Source / category</th><th>Revenue calculation</th><th>Transactions</th><th>Governance</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td><strong>{row.sourceCategory}</strong><br /><span className="business-source-secondary">{row.dataSource.sourceName}</span></td>
                  <td><strong>{row.revenueSource.sourceName}</strong><br /><span className="business-source-secondary">{row.revenueAmountField === 'NET_AMOUNT' ? 'Net Amt' : 'Taxable Amt'} · {row.businessDateField === 'EFFECTIVE_DATE' ? 'Effective Date' : 'Document Date'} · {row.currency}</span></td>
                  <td>{row.includedTransactionTypes.join(', ')}<br /><span className="business-source-secondary">Signed negative values remain negative and are counted once.</span></td>
                  <td><span className={`business-source-status ${row.isActive ? 'active' : row.approvalStatus === 'APPROVED' ? 'valid' : 'warning'}`}>{row.isActive ? 'Active' : row.approvalStatus}</span><br /><span className="business-source-secondary">{row.approvalReference || 'No approval reference'}</span></td>
                  <td>
                    <div className="business-source-row-actions business-category-rule-actions">
                      <button className="btn btn-sm business-category-action business-category-action-edit" onClick={() => {
                        setEditingId(row.id);
                        setForm({
                          dataSourceKey: row.dataSource.sourceKey,
                          sourceCategory: row.sourceCategory,
                          revenueSourceKey: row.revenueSource.sourceKey,
                          revenueAmountField: row.revenueAmountField,
                          businessDateField: row.businessDateField,
                          currency: row.currency,
                          includedTransactionTypes: row.includedTransactionTypes,
                          effectiveFrom: row.effectiveFrom,
                          effectiveTo: row.effectiveTo,
                        });
                      }}>Edit</button>
                      {!row.isActive && <button className="btn btn-sm business-category-action business-category-action-approve" onClick={() => { setApprovalId(row.id); setApprovalReference(''); }}>Approve</button>}
                      {row.isActive && <button className="btn btn-sm business-category-action business-category-action-deactivate" onClick={() => void deactivateBusinessRevenueCategoryMapping(row.id, token).then(load).catch((error) => setAlert({ type: 'error', title: 'Deactivation failed', message: message(error) }))}>Deactivate</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={5}>No governed category rules have been created.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {approvalId && (
        <div className="business-source-modal-overlay" role="dialog" aria-modal="true">
          <div className="business-file-import-modal">
            <div className="business-source-modal-header"><h3>Approve category rule</h3><p>You must be different from the maker.</p></div>
            <div className="business-file-import-modal-body">
              <label className="business-source-field"><span>Approval reference</span><input value={approvalReference} onChange={(event) => setApprovalReference(event.target.value)} maxLength={255} /></label>
            </div>
            <div className="business-source-modal-footer">
              <button className="btn btn-outline" onClick={() => setApprovalId(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={!approvalReference.trim()} onClick={() => void approve()}>Approve and activate</button>
            </div>
          </div>
        </div>
      )}
      {alert && <AdminAlertModal {...alert} onClose={() => setAlert(null)} />}
    </section>
  );
}

function message(error: unknown) {
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}
